/**
 * WS-P / D-28: what this device can actually do with a template row.
 *
 * The badge used to be `onDevice: (t) => !hidden.has(t.templateId)` — the localStorage hide-list
 * that `removeFromDevice` writes. So a template this device had NEVER synced, or had synced with
 * no bake, rendered as **on this device** (AUD-8 LB-8). One flag was answering three questions,
 * and the operator's only evidence for "it works here" was a row that could not say otherwise.
 *
 * Kept as a PURE function, deliberately: the whole precedence table is then one test, and the
 * list page's job shrinks to gathering the four inputs.
 */

export type DevicePresence =
  | "ready"           // record + every saved view's bake present locally
  | "removed"         // the operator removed it from this device (the hide-list)
  | "not-synced"      // never synced here
  | "bake-missing"    // synced, the cloud says a bake EXISTS, this device does not have it
  | "not-published";  // the cloud version has no bake at all — no device can render it fully

export interface DevicePresenceInput {
  /** The operator pressed "Remove from this device" (the localStorage hide-list). */
  hidden: boolean;
  /** A local record for this template exists (it was synced here at some point). */
  synced: boolean;
  /**
   * What the CLOUD says about the current version's bake (P-3's `has_baked_assets`).
   * `undefined` = an older cloud did not say, and must never become "not-published": that
   * accuses the authoring machine of a fault it does not have, and withholds the Sync action
   * that would fix the real problem.
   */
  hasBakedAssets: boolean | undefined;
  /** Every saved view's mockup is present in this device's persisted store. */
  bakesLocal: boolean;
}

export function devicePresence(input: DevicePresenceInput): DevicePresence {
  // 1. An explicit operator act outranks everything — the one state the old flag got right.
  if (input.hidden) return "removed";
  // 2. Nothing was ever downloaded here, so nothing local can be missing yet.
  if (!input.synced) return "not-synced";
  // 3. The cloud is authoritative about the cloud. Note this outranks the local probe: even
  //    with stale local bytes, what OTHER devices get is what the badge describes, and that is
  //    the question the operator is actually asking.
  if (input.hasBakedAssets === false) return "not-published";
  // 4. Published (or unknown) but absent here.
  if (!input.bakesLocal) return "bake-missing";
  return "ready";
}

/** How one presence renders, in WS-B's status vocabulary. `null` = no badge (the good state). */
export interface PresenceBadge {
  label: string;
  /** WS-B's tiers: neutral = a state chosen or not yet reached; warn = a render would be wrong. */
  className: string;
  /** Whether offering Sync on this row would actually help. */
  canSync: boolean;
  title: string;
}

/**
 * `satisfies Record<DevicePresence, …>` is the census: a new member without an entry is a
 * `tsc` error, not a row that silently renders nothing. Same guard as `bakeDiagnostics.ts` —
 * a vocabulary that can rot is how one flag came to answer three questions.
 */
const BADGES = {
  ready: null,
  removed: {
    label: "Not on this device",
    className: "badge badge--neutral",
    canSync: true,
    title: "You removed this template from this device. Sync back to restore it.",
  },
  "not-synced": {
    label: "Not synced here",
    className: "badge badge--neutral",
    canSync: true,
    title: "This template has never been synced to this device. Press Sync to download it.",
  },
  "bake-missing": {
    label: "Mock-up not downloaded",
    className: "badge badge--warn",
    canSync: true,
    title:
      "A mock-up was published for this template but has not reached this device, so renders " +
      "here would be design-only. Press Sync to download it.",
  },
  "not-published": {
    label: "Mock-up not published",
    className: "badge badge--warn",
    // Syncing cannot conjure a bake the cloud never received: it would 404 forever, and
    // offering it points the operator at the wrong machine.
    canSync: false,
    title:
      "No mock-up was ever uploaded for this template, so every device renders it design-only. " +
      "Syncing will not help — re-open it on the machine that baked it and let the mock-up " +
      "sync finish.",
  },
} satisfies Record<DevicePresence, PresenceBadge | null>;

export function presenceBadge(presence: DevicePresence): PresenceBadge | null {
  return BADGES[presence];
}

/**
 * Whether a row in this state should still carry the legacy `data-on-device="false"` +
 * `data-testid="template-off-device"` markers.
 *
 * `removed`, `not-synced` and `bake-missing` keep them so WS-D2's layout probe and every
 * existing test go on passing; `not-published` is a NEW state that never had them, and
 * claiming a template is off-device when it is sitting right here would be a fresh lie.
 */
export function marksRowOffDevice(presence: DevicePresence): boolean {
  return presence === "removed" || presence === "not-synced" || presence === "bake-missing";
}
